// Global Instructions Rule Applied!
// Detail shape of GET /capital-calls/:id and the cc__ write actions
// (cc__call_detail): call header + line items + per-commitment rows + the
// linked fund transaction + totals; the issue action additionally returns an
// `issue` envelope and revert a status/status_message pair. Keys are raw
// column names (to_jsonb of the row) plus joined labels, so the schemas are
// passthrough on the row objects while pinning the fields the UI consumes.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const CapitalCallHeaderSchema = z
  .object({
    id: z.string(),
    fund_id: z.string(),
    name: z.string().nullish(),
    call_date: z.string().nullish(),
    due_date: z.string().nullish(),
    status_id: z.number().nullish(),
    trashed: z.boolean().nullish(),
    fund_name: z.string().nullish(),
    functional_currency_id: z.string().nullish(),
    currency_symbol: z.string().nullish(),
    currency_code: z.string().nullish(),
    status_label: z.string().nullish(),
    counts_as_called: z.boolean().nullish(),
    // consolidated-fund links: set = lifecycle belongs to the group form
    fund_group_call_id: z.string().nullish(),
    fund_group_transfer_id: z.string().nullish(),
    prog__closed_commitments_total: z.number().nullish(),
    // resolved structured-call total (the gross ask; NULL for bottom-up calls)
    prog__call_total_amount: z.number().nullish(),
    // a set gross amount is hit exactly (the balance nets the EFFECTIVE fees,
    // absorbing the side-letter breaks); false = the balance nets the
    // HEADLINE fees (a % of committed call, or one computed before the rule)
    gross_amount_exact: z.boolean().nullish(),
    total_amount: z.number().nullish(),
    total_percentage: z.number().nullish(),
    // "Include management fees" / "Include expenses": true = the fee lines /
    // the expenses line are part of the call amount, false = added on top;
    // null = the fund-terms rule (inside, except on a % of committed call
    // when the commitment excludes them)
    fees_in_total: z.boolean().nullish(),
    expenses_in_total: z.boolean().nullish(),
    // what the call is sized on (spec A3.9 addendum part 2): 'consolidated'
    // = the primary-fund call of a consolidated call (gross or equalization),
    // sized on each investor's Consolidated Commitment; 'transfer' = a
    // satellite call derived by an intercompany transfer; null = standalone
    prog__consolidated_basis: z.enum(['consolidated', 'transfer']).nullish(),
    // the consolidated fund's member fund names, primary first
    prog__consolidated_fund_names: z.array(z.string()).nullish(),
    // the calculation record (spec A3.12): the inputs changed since the last
    // Compute, or the call was never computed - Issue refuses it
    needs_compute: z.boolean().nullish(),
    computed_at: z.string().nullish(),
    // a computed Draft: what differs today from the record it was computed
    // on (null when nothing does) - a warning, never a block
    prog__changed_since_compute: z
      .array(
        z
          .object({
            code: z.string(),
            count: z.number().nullish(),
            message: z.string()
          })
          .passthrough()
      )
      .nullish(),
    // a Draft that was issued and reverted and whose amounts still stand as
    // issued: when it was issued (null for every other call)
    prog__as_issued_at: z.string().nullish(),
    // what the commitment covers (addendum part 4): whether the call is a %
    // of committed, the fund's commitment_includes_* terms as they apply,
    // and the bucket items the form classifies unsaved lines with
    prog__commitment_terms: z
      .object({
        pct_sized: z.boolean().nullish(),
        includes_mgmt_fees: z.boolean().nullish(),
        includes_expenses: z.boolean().nullish(),
        fees_item_type_id: z.string().nullish(),
        expenses_item_type_id: z.string().nullish(),
        combined_item_type_id: z.string().nullish()
      })
      .passthrough()
      .nullish()
  })
  .passthrough()

// The intercompany-transfer context of a call derived by a transfer
// (fgc__transfer_call_context): the call-form banner reads it.
export const CapitalCallTransferPoolBucketSchema = z
  .object({
    key: z.string(),
    label: z.string().nullish(),
    prog__called: z.number(),
    prog__transferred: z.number(),
    prog__retained: z.number(),
    prog__retained_fmt: z.string().nullish(),
    prog__in_draft: z.number()
  })
  .passthrough()

export const CapitalCallTransferContextSchema = z
  .object({
    fund_group_transfers__id: z.string(),
    fund_groups__id: z.string().nullish(),
    fund_groups__name: z.string().nullish(),
    primary_fund_name: z.string().nullish(),
    satellite_fund_name: z.string().nullish(),
    fund_group_calls__id: z.string().nullish(),
    source_label: z.string().nullish(),
    is_equalization: z.boolean().nullish(),
    // Draft (the call) / Booked (the intercompany pair)
    status_label: z.string().nullish(),
    notes: z.string().nullish(),
    fee_period_from: z.string().nullish(),
    fee_period_to: z.string().nullish(),
    // SBIC Regulatory Capital at the fee period start: booked vs. with this
    // transfer's per-LP totals paid in on the call date
    regulatory_capital_before: z.number().nullish(),
    regulatory_capital_before_fmt: z.string().nullish(),
    regulatory_capital_after: z.number().nullish(),
    regulatory_capital_after_fmt: z.string().nullish(),
    assumed_leverage_before: z.number().nullish(),
    assumed_leverage_after: z.number().nullish(),
    pool: z
      .object({ buckets: z.array(CapitalCallTransferPoolBucketSchema).optional().default([]) })
      .passthrough()
      .nullish()
  })
  .passthrough()

export const CapitalCallItemSchema = z
  .object({
    id: z.string(),
    capital_call_id: z.string(),
    item_type_id: z.string(),
    item_type_label: z.string().nullish(),
    description: z.string().nullish(),
    amount: z.number().nullish(),
    percentage: z.number().nullish(),
    calculated_amount: z.number().nullish(),
    override_amount: z.number().nullish(),
    currency_id: z.string().nullish(),
    mgmt_fee_period_from: z.string().nullish(),
    mgmt_fee_period_to: z.string().nullish(),
    display_order: z.number().nullish(),
    // the line rate as computed (spec A3.12): on a fee line the fee at the
    // full rate / the fee-paying base, shown in the allocation table's header
    called_rate: z.number().nullish(),
    rate_base_total: z.number().nullish(),
    prog__item_effective_amount: z.number().nullish(),
    prog__item_calculated_amount: z.number().nullish(),
    prog__per_commitment_total: z.number().nullish(),
    // fee lines: the line at the fund's headline rate (no side letters)
    prog__item_headline_total: z.number().nullish(),
    // the line's bucket and whether it counts toward the commitment under
    // the fund's terms (addendum part 4)
    prog__bucket: z.enum(['investment', 'expenses', 'fees']).nullish(),
    prog__counts_toward_commitment: z.boolean().nullish()
  })
  .passthrough()

export const CapitalCallPerCommitmentSchema = z
  .object({
    id: z.string(),
    capital_call_id: z.string(),
    fund_commitment_id: z.string(),
    item_type_id: z.string(),
    item_type_label: z.string().nullish(),
    // what Compute calculated - on a consolidated gross call the share by
    // Consolidated Commitment, on a transfer the share by its weights - and
    // the amount a person set instead (spec A3.12, open point 6). The row
    // still carries the retired is_split_pin column; passthrough lets it by.
    calculated_amount: z.number().nullish(),
    override_amount: z.number().nullish(),
    // the calculation record (spec A3.12): the commitment the row was sized
    // on and, on a fee row, the shares that make its fee-paying capital
    commitment_amount: z.number().nullish(),
    fee_rate_share: z.number().nullish(),
    fee_basis_share: z.number().nullish(),
    fee_paying_capital: z.number().nullish(),
    // COALESCE(override, calculated) / commitment_amount
    called_rate: z.number().nullish(),
    currency_id: z.string().nullish(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    // the investor's Consolidated Commitment - its commitment across the
    // consolidated fund's member funds; null on a standalone commitment
    fund_group_commitments__amount: z.number().nullish(),
    prog__effective_amount: z.number().nullish(),
    // fee rows: the fee at the headline rate and the LP's side-letter break
    // (headline - effective), which comes off that LP's own balance share
    headline_amount: z.number().nullish(),
    prog__fee_break: z.number().nullish()
  })
  .passthrough()

export const CapitalCallTransactionSchema = z
  .object({
    id: z.string(),
    status_label: z.string().nullish(),
    transaction_date: z.string().nullish(),
    journal_count: z.number().nullish(),
    total_debits: z.number().nullish()
  })
  .passthrough()

export const CapitalCallTotalsSchema = z
  .object({
    called_total: z.number(),
    // sum of the side-letter fee breaks: the gross ask minus this is called
    // (unless fee_break_in_full)
    fee_break_total: z.number().nullish(),
    // spec A3.9: the fund (its consolidated group's primary) has investors
    // contribute in full - fee breaks never reduce a call; they are waived
    // fees, settled per the fund's Waived Fee Settlement
    fee_break_in_full: z.boolean().nullish(),
    // on a % of committed call: the lines that do not count toward the
    // commitment, added on top of the % (addendum part 4; 0 otherwise)
    outside_commitment_total: z.number().nullish(),
    // what the FIFO paid-status attributes to this call (0 while Draft)
    paid_total: z.number().nullish()
  })
  .passthrough()

export const CapitalCallIssueResultSchema = z
  .object({
    status: z.string(),
    transaction_id: z.string().nullish(),
    journal_count: z.number().nullish(),
    total_debits: z.number().nullish()
  })
  .passthrough()

export const CapitalCallDetailSchema = z
  .object({
    call: CapitalCallHeaderSchema,
    items: z.array(CapitalCallItemSchema),
    per_commitment: z.array(CapitalCallPerCommitmentSchema),
    transaction: CapitalCallTransactionSchema.nullish(),
    totals: CapitalCallTotalsSchema,
    issue: CapitalCallIssueResultSchema.nullish(),
    status: z.string().nullish(),
    status_message: z.string().nullish(),
    // compute action: per-item engine envelopes + stale-row sweep count
    compute: z.array(z.record(z.string(), z.any())).nullish(),
    orphan_rows_trashed: z.number().nullish(),
    // clear-overrides action (Recompute runs it before compute)
    overrides_cleared: z.number().nullish(),
    // equalization wizard (cc__create_equalization_call) companion keys
    created_capital_call_id: z.string().nullish(),
    // set when the wizard ran on a consolidated fund's primary: the call's
    // equalization header on the group
    created_fund_group_call_id: z.string().nullish(),
    skipped_groups: z.array(z.record(z.string(), z.any())).nullish(),
    // rename action (cc__rename_call): booked descriptions that followed
    relabelled_transactions: z.number().nullish(),
    relabelled_journals: z.number().nullish(),
    // revert action (cc__revert_call): what the FIFO paid-status had
    // attributed to the call - those receipts read as prepayments until it
    // is reissued
    paid_attributed: z.number().nullish(),
    // a call derived by an intercompany transfer: its transfer context
    transfer: CapitalCallTransferContextSchema.nullish(),
    // compute action on a transfer-derived call with a fee line: rounds the
    // SBIC fee solver took to reach the fixed point
    fee_solver_rounds: z.number().nullish()
  })
  .strict()

export type CapitalCallHeader = z.infer<typeof CapitalCallHeaderSchema>
export type CapitalCallItem = z.infer<typeof CapitalCallItemSchema>
export type CapitalCallPerCommitment = z.infer<typeof CapitalCallPerCommitmentSchema>
export type CapitalCallTransaction = z.infer<typeof CapitalCallTransactionSchema>
export type CapitalCallDetail = z.infer<typeof CapitalCallDetailSchema>
export type CapitalCallTransferContext = z.infer<typeof CapitalCallTransferContextSchema>

export function validateCapitalCallDetail(data: unknown): CapitalCallDetail {
  try {
    return validateWithZodSchema(CapitalCallDetailSchema, data, 'capital call detail')
  } catch (error) {
    throw new Error(`Capital call detail validation failed: ${error}`)
  }
}

/** Save payload sent to POST /capital-calls (cc__save_call). */
export interface CapitalCallSavePayload {
  id?: string
  fund_id?: string
  name?: string | null
  call_date?: string | null
  due_date?: string | null
  /** structured-call total: at most one of the two is set */
  total_percentage?: number | null
  total_amount?: number | null
  /** "Include management fees": the fee lines inside the call amount (false = on top, null = fund-terms rule) */
  fees_in_total?: boolean | null
  /** "Include expenses": the expenses line inside the call amount (false = on top, null = fund-terms rule) */
  expenses_in_total?: boolean | null
  items?: Array<{
    id?: string
    item_type_id: string
    description?: string | null
    amount?: number | null
    percentage?: number | null
    override_amount?: number | null
    currency_id?: string | null
    mgmt_fee_period_from?: string | null
    mgmt_fee_period_to?: string | null
    is_balance?: boolean
    catchup_kind_id?: number | null
    display_order?: number
  }>
  per_commitment_overrides?: Array<{
    id: string
    override_amount: number | null
  }>
}

// -- fund-tab reads --------------------------------------------------------

export const FundPaymentStatusRowSchema = z
  .object({
    fund_commitments__id: z.string(),
    fund_commitments__amount: z.number().nullish(),
    fund_commitments__amount_fmt: z.string().nullish(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    capital_calls__id: z.string().nullish(),
    capital_calls__name: z.string().nullish(),
    capital_calls__call_date: z.string().nullish(),
    capital_calls__call_date_fmt: z.string().nullish(),
    prog__called_amount: z.number().nullish(),
    prog__called_amount_fmt: z.string().nullish(),
    prog__paid_amount: z.number().nullish(),
    prog__paid_amount_fmt: z.string().nullish(),
    prog__outstanding: z.number().nullish(),
    prog__outstanding_fmt: z.string().nullish()
  })
  .strict()

export type FundPaymentStatusRow = z.infer<typeof FundPaymentStatusRowSchema>

export function validateFundPaymentStatusRows(data: unknown): FundPaymentStatusRow[] {
  try {
    return validateWithZodSchema(FundPaymentStatusRowSchema, data, 'fund payment status row', { isArray: true })
  } catch (error) {
    throw new Error(`Fund payment status validation failed: ${error}`)
  }
}

export const FundContributionRowSchema = z
  .object({
    capital_contributions__id: z.string(),
    fund_commitments__id: z.string(),
    funds__id: z.string().nullish(),
    investors__id: z.string().nullish(),
    investors__name: z.string().nullish(),
    capital_contributions__amount: z.number().nullish(),
    capital_contributions__amount_fmt: z.string().nullish(),
    capital_contributions__payment_date: z.string().nullish(),
    capital_contributions__payment_date_fmt: z.string().nullish(),
    // set only on a transfer-generated contribution, which has no per-row
    // receipt - the editor and the delete are both disabled on those
    capital_contributions__fund_group_transfer_id: z.string().nullish(),
    fund_transactions__id: z.string().nullish(),
    cfg_transaction_statuses__label: z.string().nullish()
  })
  .strict()

export type FundContributionRow = z.infer<typeof FundContributionRowSchema>

export function validateFundContributionRows(data: unknown): FundContributionRow[] {
  try {
    return validateWithZodSchema(FundContributionRowSchema, data, 'fund contribution row', { isArray: true })
  } catch (error) {
    throw new Error(`Fund contribution validation failed: ${error}`)
  }
}

/** One row, from view__contribution_detail — the payment editor's own read. */
export function validateFundContributionRow(data: unknown): FundContributionRow {
  try {
    return validateWithZodSchema(FundContributionRowSchema, data, 'fund contribution row')
  } catch (error) {
    throw new Error(`Fund contribution validation failed: ${error}`)
  }
}
